import { useEffect, useMemo, useState } from "react";
import RightTimeApi from "@/API";
import { FaPlusSquare } from "react-icons/fa";
import { CiEdit } from "react-icons/ci";
import { toast } from "react-toastify";
import EmailTemplateEditModal from "@modals/EmailTemplateEditModal";
import type { EmailTemplate } from "@/utils/emailTemplate";

export default function AdminEmailTab(props: { tabData: { activeTab?: string; updateSpinner?: (v: boolean) => void } }) {
  const { activeTab, updateSpinner } = props.tabData;
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);

  const sortedTemplates = useMemo(
    () => [...templates].sort((a, b) => (a.identifier || "").localeCompare(b.identifier || "")),
    [templates]
  );

  useEffect(() => {
    if (activeTab === "email") {
      loadTemplatesAsync();
    }
  }, [activeTab]);

  async function loadTemplatesAsync() {
    updateSpinner?.(true);
    try {
      const resp = await RightTimeApi.getAsync("/EmailTemplate/System");
      if (resp?.ok && resp.jsonData) {
        setTemplates(resp.jsonData);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to load email templates.");
    } finally {
      updateSpinner?.(false);
    }
  }

  function openAdd() {
    setSelectedTemplate({
      id: 0,
      identifier: "",
      name: "",
      subject: "",
      htmlBody: "",
      textBody: "",
      parameterSchema: "[]",
      isActive: true,
      scope: "system",
    });
    setIsEditOpen(true);
  }

  function openEdit(template: EmailTemplate) {
    setSelectedTemplate(template);
    setIsEditOpen(true);
  }

  if (activeTab !== "email") return null;

  return (
    <>
      <div className="tableWrapper dimensionTable emailTabTemplates">
        <table>
          <caption>
            <span className="title">System Email Templates</span>
            <span className="actionIcon plusIcon captionRight" onClick={openAdd} title="Add template">
              <FaPlusSquare />
            </span>
          </caption>
          <thead>
            <tr>
              <th>Identifier</th>
              <th>Name</th>
              <th>Subject</th>
              <th>Active</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {sortedTemplates.map((template) => (
              <tr key={template.id || template.identifier}>
                <td>{template.identifier}</td>
                <td>{template.name}</td>
                <td>{template.subject}</td>
                <td>{template.isActive === false ? "No" : "Yes"}</td>
                <td>
                  <CiEdit className="actionIcon" title="Edit" onClick={() => openEdit(template)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <EmailTemplateEditModal
        isOpen={isEditOpen}
        template={selectedTemplate}
        isSystemScope
        onClose={() => setIsEditOpen(false)}
        onSaved={loadTemplatesAsync}
      />
    </>
  );
}
